import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SvgIcons, PreLoading, Badge, PoiSearchAutoComplete, CustomSlider, TourCard, FormTemplateLogin, FormTemplateRegister, CloseIconButton2 } from "@tripian/react";
import Model from "@tripian/model";
import { api } from "@tripian/core";
import useDiscoverSearchPoi from "../../hooks/useDiscoverSearchPoi";
import useTranslate from "../../hooks/useTranslate";
import usePoiCategory from "../../hooks/usePoiCategory";
import useDiscoverSearchAutoComplete from "../../hooks/useDiscoverSearchAutoComplete";
import useProgressiveTourApi from "../../hooks/useProgressiveTourApi";
import useTourCategories from "../../hooks/useTourCategories";
import useCities from "../../hooks/useCities";
import useAuth from "../../hooks/useAuth";
import useUser from "../../hooks/useUser";
import useFavorite from "../../hooks/useFavorite";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useSelector } from "react-redux";
import ICombinedState from "../../redux/model/ICombinedState";
import { REGISTER, TRIPS, TOUR_DETAIL, CITY_INFO } from "../../constants/ROUTER_PATH_TITLE";
import { saveLocalStorageToken } from "../../App/AppWrapper/localStorages";
import moment from "moment";
import viatorLogo from "./img/favicon-viator.png";
import ValaiDateRangePicker from "./components/ValaiDateRangePicker";
import * as gtag from "../../gtag";

// ── Preset Configuration ──────────────────────────────────
interface PresetConfig {
    id: string;
    title: string;
    subtitle: string;
    type: string;
    keywords?: string;
}

const PRESETS: Record<string, PresetConfig> = {
    // ── Sports ──────────────────────────────────
    'sports-nba-chicago': {
        id: 'sports-nba-chicago',
        title: 'NBA in Chicago',
        subtitle: 'Chicago Bulls, United Center & more NBA experiences',
        type: 'sports',
        keywords: 'NBA',
},
    'sports-mlb-chicago': {
        id: 'sports-mlb-chicago',
        title: 'MLB in Chicago',
        subtitle: 'Chicago Cubs, Wrigley Field & more MLB experiences',
        type: 'sports',
        keywords: 'MLB',
    },
    'sports-nhl-chicago': {
        id: 'sports-nhl-chicago',
        title: 'NHL in Chicago',
        subtitle: 'Chicago Blackhawks, United Center & more NHL experiences',
        type: 'sports',
        keywords: 'NHL',
    },
    'sports-mls-chicago': {
        id: 'sports-mls-chicago',
        title: 'MLS in Chicago',
        subtitle: 'Chicago Fire, Soldier Field & more MLS experiences',
        type: 'sports',
        keywords: 'MLS',
    },
    'sports-more-chicago': {
        id: 'sports-more-chicago',
        title: 'Sports in Chicago',
        subtitle: 'Games, tickets & more sports experiences in Chicago',
        type: 'sports',
        keywords: 'sports',
    },

    // ── Music ───────────────────────────────────
    'music-blues-jazz-chicago': {
        id: 'music-blues-jazz-chicago',
        title: 'Blues & Jazz in Chicago',
        subtitle: 'Chicago Blues, Jazz Showcase & more blues & jazz experiences',
        type: 'music',
        keywords: 'blues jazz',
    },
    'music-rock-pop-chicago': {
        id: 'music-rock-pop-chicago',
        title: 'Rock & Pop in Chicago',
        subtitle: 'House of Blues, Metro Chicago & more rock & pop experiences',
        type: 'music',
        keywords: 'rock pop',
    },
    'music-rap-hiphop-chicago': {
        id: 'music-rap-hiphop-chicago',
        title: 'Rap & Hip Hop in Chicago',
        subtitle: 'Rap concerts, hip hop events & experiences in Chicago',
        type: 'music',
        keywords: 'rap hip hop',
    },
    'music-electronic-dance-chicago': {
        id: 'music-electronic-dance-chicago',
        title: 'Electronic & Dance in Chicago',
        subtitle: 'Radius Chicago, Concord Music Hall & more electronic & dance experiences',
        type: 'music',
        keywords: 'electronic dance',
    },
    'music-country-folk-chicago': {
        id: 'music-country-folk-chicago',
        title: 'Country & Folk in Chicago',
        subtitle: "Joe's on Weed St & more country & folk experiences",
        type: 'music',
        keywords: 'country folk',
    },
    'music-more-chicago': {
        id: 'music-more-chicago',
        title: 'Music in Chicago',
        subtitle: 'Concerts, live music & more music experiences in Chicago',
        type: 'music',
        keywords: 'music',
    },

    // ── Theater ─────────────────────────────────
    'theater-ballet-dance-chicago': {
        id: 'theater-ballet-dance-chicago',
        title: 'Ballet & Dance in Chicago',
        subtitle: 'Joffrey Ballet, Auditorium Theatre & more ballet & dance experiences',
        type: 'theater',
        keywords: 'ballet dance',
    },
    'theater-broadway-chicago': {
        id: 'theater-broadway-chicago',
        title: 'Broadway in Chicago',
        subtitle: 'CIBC Theatre, Cadillac Palace Theatre & more Broadway experiences',
        type: 'theater',
        keywords: 'broadway',
    },
    'theater-musicals-chicago': {
        id: 'theater-musicals-chicago',
        title: 'Musicals in Chicago',
        subtitle: 'CIBC Theatre & more musical experiences in Chicago',
        type: 'theater',
        keywords: 'musicals',
    },
    'theater-magic-chicago': {
        id: 'theater-magic-chicago',
        title: 'Magic in Chicago',
        subtitle: 'Magic shows, performances & experiences in Chicago',
        type: 'theater',
        keywords: 'magic',
    },
    'theater-opera-chicago': {
        id: 'theater-opera-chicago',
        title: 'Opera in Chicago',
        subtitle: 'Lyric Opera, Civic Opera House & more opera experiences',
        type: 'theater',
        keywords: 'opera',
    },
    'theater-more-chicago': {
        id: 'theater-more-chicago',
        title: 'Theater in Chicago',
        subtitle: 'Shows, performances & more theater experiences in Chicago',
        type: 'theater',
        keywords: 'theater',
    },
};

// Provider constants
const TOUR_PROVIDERS = [
    { id: Model.PROVIDER_ID.VIATOR, name: "Viator" },
    { id: Model.PROVIDER_ID.VICTORY, name: "Victory Live" },
];

const DetailPoiAndTour = () => {
    const { cityId } = useParams<{ cityId: string }>();
    const [searchParams] = useSearchParams();
    const { t, onSelectedLangCode, langCode } = useTranslate();
    const navigate = useNavigate();
    const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
    const theme = getLocalData();

    // Preset detection from URL
    const presetId = searchParams.get('preset');
    const activePreset = presetId ? PRESETS[presetId] : undefined;

    const { searchResults, loading: searchLoading, pagination, searchPoi } = useDiscoverSearchPoi(cityId ? parseInt(cityId) : undefined);
    const { poiCategoryGroups, loadingPoiCategories } = usePoiCategory();
    const { searchPoiAutoCompleteTags } = useDiscoverSearchAutoComplete(cityId ? parseInt(cityId) : undefined);
    const { cities } = useCities();

    // Get city information
    const cityInfo = useMemo(() => {
        if (!cityId || !cities) return undefined;
        return cities.find(city => city.id === parseInt(cityId));
    }, [cityId, cities]);

    // Tour date selection state (preset mode defaults to 30 days range)
    const [tourStartDate, setTourStartDate] = useState<string>(moment().format("YYYY-MM-DD"));
    const [tourEndDate, setTourEndDate] = useState<string>(moment().add(activePreset ? 29 : 1, "days").format("YYYY-MM-DD"));

    // Viator and Rezdy tours - memoize parameters to prevent re-fetching
    const tourParams = useMemo(() => {
        return {
            cityName: cityInfo?.name,
            cityId: cityId || undefined,
            today: tourStartDate,
            futureDate: tourEndDate
        };
    }, [cityInfo?.name, cityId, tourStartDate, tourEndDate]);

    // Tour categories from API
    const { categories: tourCategories, loading: loadingTourCategories } = useTourCategories(
        tourParams.cityId
    );

    // Flatten all category tags for easy lookup (deduplicated by tag)
    const allCategoryItems = useMemo(() => {
        const seen = new Set<string>();
        const items: Model.TourCategoryItem[] = [];
        tourCategories.forEach((cat) => {
            cat.subCategories?.forEach((sub) => {
                sub.categories.forEach((item) => {
                    if (!seen.has(item.tag)) {
                        seen.add(item.tag);
                        items.push(item);
                    }
                });
            });
        });
        return items;
    }, [tourCategories]);

    // Handle tour click - open tour detail page in new tab
    const handleTourClick = (
        provider: Model.PROVIDER_ID,
        tourId: string,
        additionalData?: any
    ) => {
        gtag.googleAnalyticsEvent("tour_modal_open", { provider, tour_id: tourId });
        if (provider === Model.PROVIDER_ID.VIATOR) {
            const tourDetailPath = `${TOUR_DETAIL.PATH}?cityId=${cityId}&provider=${provider}&tourId=${tourId}`;
            window.open(tourDetailPath, "_blank");
        } else if (provider === Model.PROVIDER_ID.VICTORY) {
            const tourDetailPath = `/tour?cityId=${cityId}&provider=${provider}&tourId=${tourId}`;
            window.open(tourDetailPath, "_blank");  // TourDetailPage
        } else {
            const tourDetailPath = `${TOUR_DETAIL.PATH}?cityId=${cityId}&provider=${provider}&tourId=${tourId}`;
            window.open(tourDetailPath, "_blank");
        }
    };

    // State management - check URL for initial tab (preset forces tours-tickets)
    const initialTab = (searchParams.get('tab') === 'tours-tickets' || activePreset) ? 'tours-tickets' : 'things-to-do';
    const [activeTab, setActiveTab] = useState<'things-to-do' | 'tours-tickets'>(initialTab);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [poiCategorySearchText, setPoiCategorySearchText] = useState<string>("");
    const [selectedPoiCategories, setSelectedPoiCategories] = useState<Model.PoiCategory[]>([]);
    const [sortBy, setSortBy] = useState<string>("");
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [activeMainCategory] = useState<string>("all");
    const [selectedRating, setSelectedRating] = useState<number | null>(null); // null = all, 5 = 5 stars, 4 = 4+ stars, etc.

    // Helper to convert rating to min,max format (e.g., 4 -> "4,5", 3 -> "3,5", 5 -> "5")
    const getRatingString = (rating: number | null): string | undefined => {
        if (!rating) return undefined;
        if (rating === 5) return "5";
        return `${rating},5`;
    };
    const [autoCompleteTags, setAutoCompleteTags] = useState<{ value: string; label: string }[]>([]);
    const [autoCompleteLoading, setAutoCompleteLoading] = useState<boolean>(false);

    // Pagination - track current page
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);

    // Sidebar accordion states
    const [openSidebarGroups, setOpenSidebarGroups] = useState<string[]>([]);

    // Tours & Tickets tab state
    const [selectedTourCategories, setSelectedTourCategories] = useState<string[]>([]);
    const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
    const [tourSearchText, setTourSearchText] = useState<string>(activePreset?.keywords || "");
    const [committedTourSearch, setCommittedTourSearch] = useState<string>(activePreset?.keywords || "");
    const [showTourFilters, setShowTourFilters] = useState<boolean>(false);
    const [currentTourPage, setCurrentTourPage] = useState<number>(1);
    const [categoryFilterText, setCategoryFilterText] = useState<string>("");

    // Filter category items by selected providers (show all if providers field not populated)
    const visibleCategoryItems = useMemo(() => {
        if (selectedProviders.length === 0) return [];
        return allCategoryItems.filter(item =>
            !item.providers || item.providers.length === 0 || item.providers.some(p => selectedProviders.includes(p))
        );
    }, [allCategoryItems, selectedProviders]);

    // Filter category items by search text
    const filteredCategoryItems = useMemo(() => {
        if (!categoryFilterText.trim()) return visibleCategoryItems;
        const search = categoryFilterText.trim().toLowerCase();
        return visibleCategoryItems.filter(item => item.name.toLowerCase().includes(search));
    }, [visibleCategoryItems, categoryFilterText]);

    // Grouped categories — provider and search filters applied, preserving hierarchy
    const filteredGroupedCategories = useMemo(() => {
        const search = categoryFilterText.trim().toLowerCase();
        return tourCategories
            .map(cat => ({
                ...cat,
                subCategories: (cat.subCategories ?? [])
                    .map(sub => ({
                        ...sub,
                        categories: sub.categories.filter(item => {
                            if (selectedProviders.length > 0) {
                                if (item.providers?.length > 0 && !item.providers.some(p => selectedProviders.includes(p))) return false;
                            }
                            if (search && !item.name.toLowerCase().includes(search)) return false;
                            return true;
                        })
                    }))
                    .filter(sub => sub.categories.length > 0)
            }))
            .filter(cat => (cat.subCategories ?? []).length > 0);
    }, [tourCategories, selectedProviders, categoryFilterText]);

    // Check if all visible categories are selected
    const isAllSelected = useMemo(() => {
        if (visibleCategoryItems.length === 0) return true;
        return visibleCategoryItems.every(item => selectedTourCategories.includes(item.tag));
    }, [visibleCategoryItems, selectedTourCategories]);

    // Build categories string for API
    const categoriesParam = useMemo(() => {
        if (selectedTourCategories.length === 0 || isAllSelected) return undefined;
        return selectedTourCategories.join(",");
    }, [selectedTourCategories, isAllSelected]);

    const tourKeywords = committedTourSearch.trim().length >= 2 ? committedTourSearch.trim() : undefined;

    // Single useProgressiveTourApi call with comma-separated providerId
    const combinedProviderId = selectedProviders.length > 0
        ? selectedProviders.map(String).join(",")
        : undefined;

    const {
        tours: combinedTours,
        loading: combinedLoading,
        hasNextPage,
        totalLoaded,
        isExhausted,
        totalPages: totalTourPages,
    } = useProgressiveTourApi({
        cityId: tourParams.cityId,
        providerId: combinedProviderId,
        lat: cityInfo?.coordinate?.lat,
        lng: cityInfo?.coordinate?.lng,
        date: tourParams.today,
        to: tourParams.futureDate,
        adults: 2,
        currency: "usd",
        lang: "en",
        radius: 10,
        keywords: tourKeywords,
        categories: categoriesParam,
        currentPage: currentTourPage,
        pageSize: 10,
    });

    const isLoggedIn = useSelector((state: ICombinedState) => state.user.isLoggedIn);

    // Auth hooks
    const { loggedIn, registerEmail } = useAuth();
    const { userFetch } = useUser();

    // Auth modal state
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalType, setAuthModalType] = useState<'register' | 'login'>('login');
    const [pendingFavoritePoiId, setPendingFavoritePoiId] = useState<string | null>(null);

    // Favorites hook - real API
    const { favorites, favoriteAdd, favoriteDelete, loadingFavoritePoi, favoritesFetch } = useFavorite();

    // Fetch favorites on mount or when city/auth changes
    useEffect(() => {
        if (cityId && isLoggedIn) {
            favoritesFetch();
        }
    }, [cityId, isLoggedIn, favoritesFetch]);

    // Check if POI is favorited
    const isPoiFavorite = useCallback((poiId: string) => {
        return favorites?.some(fav => fav.poiId === poiId) ?? false;
    }, [favorites]);

    // Get favorite ID for a POI
    const getFavoriteId = useCallback((poiId: string) => {
        return favorites?.find(fav => fav.poiId === poiId)?.id;
    }, [favorites]);

    // Handle favorite toggle
    const handleFavoriteToggle = (poiId: string) => {
        if (!isLoggedIn) {
            // Save pending favorite and show auth modal
            setPendingFavoritePoiId(poiId);
            setAuthModalType('login');
            setShowAuthModal(true);
            return;
        }

        // Toggle favorite status using real API
        if (isPoiFavorite(poiId)) {
            const favoriteId = getFavoriteId(poiId);
            if (favoriteId) {
                favoriteDelete(favoriteId, poiId);
            }
        } else {
            favoriteAdd(poiId);
        }
    };

    // Login success callback
    const callbackSuccessLogin = async (token: Model.Token) => {
        saveLocalStorageToken(token);
        loggedIn();
        await userFetch(true);
        setShowAuthModal(false);

        // Process pending favorite if any
        if (pendingFavoritePoiId) {
            favoriteAdd(pendingFavoritePoiId);
            setPendingFavoritePoiId(null);
        }
    };

    // Register success callback
    const callbackSuccessRegister = async (userEmail: string, token: Model.Token) => {
        saveLocalStorageToken(token);
        loggedIn();
        await userFetch(true);
        setShowAuthModal(false);

        // Process pending favorite if any
        if (pendingFavoritePoiId) {
            favoriteAdd(pendingFavoritePoiId);
            setPendingFavoritePoiId(null);
        }
    };

    const onClickSignUp = () => {
        if (isLoggedIn) {
            navigate(TRIPS.PATH);
        } else {
            if (window.tconfig.SHOW_REGISTER) {
                navigate(REGISTER.PATH);
            }
        }
    };

    // Initialize tags from URL params
    // selectedPoiCategories starts empty (UI shows no checkboxes selected)
    // but effectiveCategoryIds will use allPoiCategoryIds for API requests
    useEffect(() => {
        const tagsParam = searchParams.get('tags');
        if (tagsParam) {
            setSelectedTags(tagsParam.split(','));
        }
    }, [searchParams]);

    // Get all category IDs (for API when no filter selected)
    const allPoiCategoryIds = useMemo(() => {
        return poiCategoryGroups.flatMap(group => group.categories).map(cat => cat.id);
    }, [poiCategoryGroups]);

    // Get selected category IDs
    const selectedPoiCategoryIds = useMemo(() => {
        return selectedPoiCategories.map(cat => cat.id);
    }, [selectedPoiCategories]);

    // Effective category IDs for API - use all if none selected
    const effectiveCategoryIds = useMemo(() => {
        return selectedPoiCategoryIds.length > 0 ? selectedPoiCategoryIds : allPoiCategoryIds;
    }, [selectedPoiCategoryIds, allPoiCategoryIds]);

    // Fetch autocomplete tags
    useEffect(() => {
        if (effectiveCategoryIds && effectiveCategoryIds.length > 0) {
            setAutoCompleteLoading(true);
            searchPoiAutoCompleteTags(effectiveCategoryIds)
                .then((options: { value: string; label: string }[]) => {
                    setAutoCompleteTags(options);
                })
                .catch(() => {
                    setAutoCompleteTags([]);
                })
                .finally(() => {
                    setAutoCompleteLoading(false);
                });
        } else {
            setAutoCompleteTags([]);
            setAutoCompleteLoading(false);
        }
    }, [searchPoiAutoCompleteTags, effectiveCategoryIds]);

    // Trigger search when filters or tags change
    useEffect(() => {
        if (effectiveCategoryIds.length > 0) {
            setCurrentPage(1); // Reset to page 1 when filters change
        }
    }, [effectiveCategoryIds, selectedTags, selectedRating]);

    // Trigger search when page changes
    useEffect(() => {
        if (effectiveCategoryIds.length > 0) {
            searchPoi({
                searchPoiCategories: effectiveCategoryIds,
                showOffersOnly: false,
                showBookingsOnly: false,
                searchText: selectedTags.length > 0 ? selectedTags.join('|') : undefined,
                page: currentPage,
                sort: sortBy,
                rating: getRatingString(selectedRating)
            });
        }
    }, [currentPage, effectiveCategoryIds, selectedTags, sortBy, selectedRating, searchPoi]);

    const filterButtonRef = React.useRef<HTMLButtonElement>(null);
    const [popupPosition, setPopupPosition] = useState<{
        top: number;
        right: number;
        placement: "top" | "bottom";
    }>({
        top: 0,
        right: 0,
        placement: "bottom",
    });

    useEffect(() => {
        if (showFilters && filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const estimatedPopupHeight = 400;
            const safetyMargin = 20;

            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow >= estimatedPopupHeight + safetyMargin) {
                setPopupPosition({
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right,
                    placement: "bottom",
                });
            } else if (spaceAbove >= estimatedPopupHeight + safetyMargin) {
                setPopupPosition({
                    top: Math.max(100, rect.top - estimatedPopupHeight + 100),
                    right: window.innerWidth - rect.right,
                    placement: "top",
                });
            } else {
                if (spaceBelow >= spaceAbove) {
                    setPopupPosition({
                        top: rect.bottom + 8,
                        right: window.innerWidth - rect.right,
                        placement: "bottom",
                    });
                } else {
                    setPopupPosition({
                        top: Math.max(100, rect.top - estimatedPopupHeight + 100),
                        right: window.innerWidth - rect.right,
                        placement: "top",
                    });
                }
            }
        }
    }, [showFilters]);

    const mainCategoryGroups = useMemo(() => {
        return [
            { id: "all", name: t("trips.categories.all") },
            ...poiCategoryGroups.map(group => ({ id: group.name, name: group.name })),
        ];
    }, [poiCategoryGroups, t]);

    const sortedSelectedTags = useMemo(() => {
        const tagOrder = autoCompleteTags.map(tag => tag.value);
        return [...selectedTags].sort(
            (a, b) => tagOrder.indexOf(a) - tagOrder.indexOf(b)
        );
    }, [selectedTags, autoCompleteTags]);

    // Handle load more - fetch next page
    const handleLoadMore = async () => {
        setIsLoadingMore(true);
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        await searchPoi({
            searchPoiCategories: selectedPoiCategoryIds,
            showOffersOnly: false,
            showBookingsOnly: false,
            searchText: selectedTags.length > 0 ? selectedTags.join('|') : undefined,
            page: nextPage,
            rating: getRatingString(selectedRating)
        });
        setIsLoadingMore(false);
    };

    // Check if there are more pages
    const hasMore = pagination ? pagination.currentPage < pagination.totalPages : false;

    // Toggle sidebar group
    const toggleSidebarGroup = (groupName: string) => {
        setOpenSidebarGroups(prev =>
            prev.includes(groupName)
                ? prev.filter(name => name !== groupName)
                : [...prev, groupName]
        );
    };

    // Initialize all groups as open
    useEffect(() => {
        setOpenSidebarGroups(poiCategoryGroups.map(group => group.name));
    }, [poiCategoryGroups]);

    // Initialize selected providers from URL, preset, or all providers
    useEffect(() => {
        if (activePreset) {
            // Preset mode: Victory Live only
            setSelectedProviders([Model.PROVIDER_ID.VICTORY]);
            return;
        }
        const providerParam = searchParams.get('provider');
        if (providerParam === 'viator') {
            setSelectedProviders([Model.PROVIDER_ID.VIATOR]);
        } else if (providerParam === 'victory') {
            setSelectedProviders([Model.PROVIDER_ID.VICTORY]);
        } else {
            // Default to all providers
            setSelectedProviders(TOUR_PROVIDERS.map(p => p.id));
        }
    }, [searchParams, activePreset]);

    // Update page title based on active tab
    useEffect(() => {
        const title = activeTab === 'things-to-do' ? 'Things To Do' : 'Tours & Tickets';
        document.title = CITY_INFO.TITLE(title);
    }, [activeTab]);

    // Reset tour page when filters or dates change
    useEffect(() => {
        setCurrentTourPage(1);
    }, [selectedTourCategories, selectedProviders, committedTourSearch, tourStartDate, tourEndDate]);

    // Handle category toggle (multi-select)
    const handleTourCategoryToggle = (tag: string) => {
        setSelectedTourCategories(prev =>
            prev.includes(tag)
                ? prev.filter(t => t !== tag)
                : [...prev, tag]
        );
    };

    // Handle "All" checkbox toggle
    const handleTourCategoryAllToggle = () => {
        if (isAllSelected) {
            setSelectedTourCategories([]);
        } else {
            setSelectedTourCategories(visibleCategoryItems.map(item => item.tag));
        }
    };

    // Handle provider change — allow deselecting all, deselect hidden categories
    const handleProviderChange = (providerId: number) => {
        const newProviders = selectedProviders.includes(providerId)
            ? selectedProviders.filter(p => p !== providerId)
            : [...selectedProviders, providerId];
        setSelectedProviders(newProviders);

        // Deselect categories that are no longer visible with the new provider selection
        if (newProviders.length > 0) {
            const newVisibleTags = allCategoryItems
                .filter(item => !item.providers || item.providers.length === 0 || item.providers.some(p => newProviders.includes(p)))
                .map(item => item.tag);
            setSelectedTourCategories(prev => prev.filter(tag => newVisibleTags.includes(tag)));
        } else {
            setSelectedTourCategories([]);
        }
    };


    // Tour accordion states (3-level)
    const [openTourMainGroups, setOpenTourMainGroups] = useState<string[]>([]);
    const [openTourSubGroups, setOpenTourSubGroups] = useState<string[]>([]);

    // Default open all tour groups when categories load
    useEffect(() => {
        setOpenTourMainGroups(tourCategories.map(c => c.name));
        setOpenTourSubGroups(tourCategories.flatMap(c => (c.subCategories ?? []).map(s => s.name)));
    }, [tourCategories]);

    const toggleTourMainGroup = (name: string) => {
        setOpenTourMainGroups(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    };
    const toggleTourSubGroup = (name: string) => {
        setOpenTourSubGroups(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
    };

    // Group-level select all / deselect all
    const handleMainCategoryToggle = (cat: Model.TourCategory) => {
        const allTags = (cat.subCategories ?? []).flatMap(s => s.categories.map(i => i.tag));
        const allSelected = allTags.every(tag => selectedTourCategories.includes(tag));
        if (allSelected) {
            setSelectedTourCategories(prev => prev.filter(t => !allTags.includes(t)));
        } else {
            setSelectedTourCategories(prev => {
                const merged = [...prev];
                allTags.forEach(tag => { if (!merged.includes(tag)) merged.push(tag); });
                return merged;
            });
        }
    };

    const handleSubCategoryToggle = (sub: Model.TourSubCategory) => {
        const allTags = sub.categories.map(i => i.tag);
        const allSelected = allTags.every(tag => selectedTourCategories.includes(tag));
        if (allSelected) {
            setSelectedTourCategories(prev => prev.filter(t => !allTags.includes(t)));
        } else {
            setSelectedTourCategories(prev => {
                const merged = [...prev];
                allTags.forEach(tag => { if (!merged.includes(tag)) merged.push(tag); });
                return merged;
            });
        }
    };

    const tourFilterButtonRef = React.useRef<HTMLButtonElement>(null);
    const [tourPopupPosition, setTourPopupPosition] = useState<{
        top: number;
        right: number;
        placement: "top" | "bottom";
    }>({
        top: 0,
        right: 0,
        placement: "bottom",
    });

    useEffect(() => {
        if (showTourFilters && tourFilterButtonRef.current) {
            const rect = tourFilterButtonRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const estimatedPopupHeight = 400;
            const safetyMargin = 20;
            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow >= estimatedPopupHeight + safetyMargin) {
                setTourPopupPosition({
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right,
                    placement: "bottom",
                });
            } else {
                setTourPopupPosition({
                    top: Math.max(100, rect.top - estimatedPopupHeight + 100),
                    right: window.innerWidth - rect.right,
                    placement: "top",
                });
            }
        }
    }, [showTourFilters]);

    return (
        <div style={{ fontFamily: 'Inter, sans-serif' }}>
            <Navbar
                theme={theme}
                t={t}
            />
            <div className="min-h-screen bg-background-color" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="w-full mx-auto px-4 md:px-24 lg:px-28 py-6 md:py-12">
                    <div className="mx-auto py-4 md:py-8 mt-122">
                        {/* Tabs */}
                        <div className="mb-6">
                            {/* Mobile: Scrollable tabs */}
                            <div className="md:hidden overflow-x-auto scrollbar-hide border-b border-gray-200">
                                <div className="flex min-w-max">
                                    <button
                                        onClick={() => {
                                            setActiveTab('things-to-do');
                                            navigate(`?tab=things-to-do`, { replace: true });
                                        }}
                                        className={`pb-3 px-6 font-18-h font-weight-500 flex-shrink-0 text-start ${activeTab === 'things-to-do'
                                            ? 'text-black border-b-2 border-primary-color'
                                            : 'text-black hover:text-gray-700'
                                            }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className="syne-fm text-lg whitespace-nowrap">{t('valai.thingsTodo')}</span>
                                            <span className="text-xs text-gray-500 mt-1">{pagination?.total || 0} {t('trips.myTrips.itinerary.places')}</span>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => {
                                            setActiveTab('tours-tickets');
                                            navigate(`?tab=tours-tickets`, { replace: true });
                                        }}
                                        className={`pb-3 px-6 font-18-h font-weight-500 flex-shrink-0 text-start ${activeTab === 'tours-tickets'
                                            ? 'text-black border-b-2 border-primary-color'
                                            : 'text-black hover:text-gray-700'
                                            }`}
                                    >
                                        <div className="flex flex-col">
                                            <span className="syne-fm text-lg whitespace-nowrap">{t('valai.tourTickets')}</span>
                                            <span className="text-xs text-gray-500 mt-1">{isExhausted ? `${totalLoaded}` : `${totalLoaded}+`} {t('trips.myTrips.localExperiences.tourDetails.experience.tours')}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            {/* Desktop: Normal tabs */}
                            <div className="hidden md:flex border-b border-gray-200">
                                <button
                                    onClick={() => {
                                        setActiveTab('things-to-do');
                                        navigate(`?tab=things-to-do`, { replace: true });
                                    }}
                                    className={`pb-3 px-4 font-18-h font-weight-500 w-1/4 text-start ${activeTab === 'things-to-do'
                                        ? 'text-black border-b-2 border-primary-color'
                                        : 'text-black hover:text-gray-700'
                                        }`}
                                >
                                    <div className="flex flex-col">
                                        <span className="syne-fm text-2xl">{t('valai.thingsTodo')}</span>
                                        <span className="text-xs text-gray-500 mt-1">{pagination?.total || 0} {t('trips.myTrips.itinerary.places')}</span>
                                    </div>
                                </button>

                                <div className="border-l border-gray-300 my-2"></div>

                                <button
                                    onClick={() => {
                                        setActiveTab('tours-tickets');
                                        navigate(`?tab=tours-tickets`, { replace: true });
                                    }}
                                    className={`pb-3 px-4 font-18-h font-weight-500 w-1/4 text-start ${activeTab === 'tours-tickets'
                                        ? 'text-black border-b-2 border-primary-color'
                                        : 'text-black hover:text-gray-700'
                                        }`}
                                >
                                    <div className="flex flex-col">
                                        <span className="syne-fm text-2xl">{t('valai.tourTickets')}</span>
                                        <span className="text-xs text-gray-500 mt-1">{isExhausted ? `${totalLoaded}` : `${totalLoaded}+`} Tours</span>
                                    </div>
                                </button>
                            </div>

                            {/* Breadcrumb - Dynamic based on tab */}
                            <div className="mt-6">
                                <div className="text-sm text-gray-500 mb-4">
                                    {cityInfo?.name || 'City'} &gt;{' '}
                                    {activePreset ? (
                                        <>
                                            <span className="font-medium text-gray-900">Tours &amp; Tickets</span>
                                            {' '}&gt;{' '}
                                            <span className="font-medium text-gray-900">{activePreset.title}</span>
                                        </>
                                    ) : (
                                        <span className="font-medium text-gray-900">
                                            {activeTab === 'things-to-do' ? 'Things To Do' : 'Tours & Tickets'}
                                        </span>
                                    )}
                                </div>

                                {/* Preset Header */}
                                {activePreset && activeTab === 'tours-tickets' && (
                                    <div className="mb-6">
                                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 syne-fm">{activePreset.title}</h1>
                                        <p className="text-sm text-gray-500 mt-1">{activePreset.subtitle}</p>
                                    </div>
                                )}

                                {activeTab === 'things-to-do' ? (
                                    <>
                                        {/* Search Bar from DiscoverDetailPage */}
                                        <div className="flex items-center w-full gap-2">
                                            <div className="flex-1 relative w-full">
                                                <PoiSearchAutoComplete
                                                    options={autoCompleteTags}
                                                    isLoading={autoCompleteLoading && autoCompleteTags.length === 0}
                                                    selectedOptionValues={selectedTags}
                                                    onSelectedOptionChange={selectedOptions => {
                                                        setSelectedTags(selectedOptions.map(opt => opt.value));
                                                    }}
                                                    onCreateOption={input => {
                                                        if (input.length > 2 && !selectedTags.includes(input)) {
                                                            setSelectedTags([...selectedTags, input]);
                                                        }
                                                    }}
                                                    inputValue={poiCategorySearchText}
                                                    onInputChange={searchText =>
                                                        setPoiCategorySearchText(searchText)
                                                    }
                                                    onKeyDown={e => {
                                                        if (
                                                            e.key === "Enter" &&
                                                            poiCategorySearchText.trim().length > 0
                                                        ) {
                                                            const newSearchTerm = poiCategorySearchText.trim();
                                                            if (!selectedTags.includes(newSearchTerm)) {
                                                                setSelectedTags(prev => [...prev, newSearchTerm]);
                                                            }
                                                            setPoiCategorySearchText("");
                                                        }
                                                    }}
                                                    placeHolder={activeTab === 'things-to-do' ? t('destination.ai.modal.searchButton') : t('destination.ai.modal.searchButton')}
                                                    isDisabled={false}
                                                    t={t}
                                                />

                                                <SvgIcons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                                <button
                                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                    onClick={() => {
                                                        setPoiCategorySearchText("");
                                                    }}
                                                >
                                                    {poiCategorySearchText && <SvgIcons.SearchClose />}
                                                </button>
                                            </div>

                                            {/* Mobile Filter Button - Only visible on mobile */}
                                            <button
                                                ref={filterButtonRef}
                                                onClick={() => setShowFilters(!showFilters)}
                                                className="md:hidden flex items-center justify-center w-12 h-12 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                                            >
                                                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                </svg>
                                            </button>

                                            <div className="relative ml-2">


                                                {showFilters && (
                                                    <>
                                                        <div
                                                            className="fixed inset-0 z-40"
                                                            onClick={() => setShowFilters(false)}
                                                            style={{ pointerEvents: "all" }}
                                                        ></div>
                                                        <div
                                                            className="absolute w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                                            style={{
                                                                top: '100%',
                                                                right: 0,
                                                                marginTop: '8px',
                                                                maxHeight: "70vh",
                                                            }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <div className="p-4 border-b flex justify-between items-center bg-white z-10 rounded-t-lg">
                                                                <h3 className="font-semibold text-gray-800">
                                                                    {t("trips.filters.title")}
                                                                </h3>
                                                                <div className="flex items-center">
                                                                    <button
                                                                        className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 hover:text-gray-900 transition-colors mr-3"
                                                                        onClick={() => {
                                                                            setSelectedPoiCategories([]);
                                                                            setSelectedRating(null);
                                                                            setShowFilters(false);
                                                                        }}
                                                                    >
                                                                        {t("trips.filters.clearAll")}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => setShowFilters(false)}
                                                                        className="text-gray-400 hover:text-gray-700 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                                                                    >
                                                                        <SvgIcons.SearchClose
                                                                            width="18"
                                                                            height="18"
                                                                            className="text-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center rounded-full hover:bg-gray-100"
                                                                        />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                                                                <div className="p-4">
                                                                    {activeMainCategory !== "all" && (
                                                                        <div className="mb-3">
                                                                            <h4 className="font-medium mb-3 text-gray-700 border-l-4 border-primary-color pl-2">
                                                                                {
                                                                                    mainCategoryGroups.find(
                                                                                        c => c.id === activeMainCategory
                                                                                    )?.name
                                                                                }
                                                                            </h4>
                                                                            <div className="grid grid-cols-1 gap-2 pl-2">
                                                                                {poiCategoryGroups
                                                                                    .find(
                                                                                        group => group.name === activeMainCategory
                                                                                    )
                                                                                    ?.categories.map(category => (
                                                                                        <div
                                                                                            className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ml-1.5"
                                                                                            key={`filter-category-${category.id}`}
                                                                                        >
                                                                                            <div className="relative w-5 h-5">
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    id={`category-${category.id}`}
                                                                                                    checked={selectedPoiCategoryIds.includes(
                                                                                                        category.id
                                                                                                    )}
                                                                                                    onChange={() => {
                                                                                                        const newCategories =
                                                                                                            selectedPoiCategoryIds.includes(
                                                                                                                category.id
                                                                                                            )
                                                                                                                ? selectedPoiCategories.filter(
                                                                                                                    c => c.id !== category.id
                                                                                                                )
                                                                                                                : [
                                                                                                                    ...selectedPoiCategories,
                                                                                                                    category,
                                                                                                                ];

                                                                                                        setSelectedPoiCategories(
                                                                                                            newCategories
                                                                                                        );
                                                                                                    }}
                                                                                                    className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                                                                                />
                                                                                                <SvgIcons.Check2
                                                                                                    className={`absolute left-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${selectedPoiCategoryIds.includes(
                                                                                                        category.id
                                                                                                    )
                                                                                                        ? "opacity-100"
                                                                                                        : "opacity-0"
                                                                                                        }`}
                                                                                                    fill="white"
                                                                                                />
                                                                                            </div>
                                                                                            <label
                                                                                                htmlFor={`category-${category.id}`}
                                                                                                className="ml-2.5 text-gray-700 cursor-pointer text-sm"
                                                                                            >
                                                                                                {category.name}
                                                                                            </label>
                                                                                        </div>
                                                                                    ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {activeMainCategory === "all" && (
                                                                        <div>
                                                                            {poiCategoryGroups.map(group => (
                                                                                <div
                                                                                    key={`filter-group-${group.name}`}
                                                                                    className="mb-5"
                                                                                >
                                                                                    <h4 className="font-medium mb-3 text-gray-700 border-l-4 border-primary-color pl-2">
                                                                                        {group.name}
                                                                                    </h4>
                                                                                    <div className="grid grid-cols-1 gap-2 pl-2">
                                                                                        {group.categories.map(category => (
                                                                                            <div
                                                                                                className="flex items-center hover:bg-gray-50 rounded-md p-1.5 -ml-1.5"
                                                                                                key={`filter-category-${category.id}`}
                                                                                            >
                                                                                                <div className="relative w-5 h-5">
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        id={`category-${category.id}`}
                                                                                                        checked={selectedPoiCategoryIds.includes(
                                                                                                            category.id
                                                                                                        )}
                                                                                                        onChange={() => {
                                                                                                            const newCategories =
                                                                                                                selectedPoiCategoryIds.includes(
                                                                                                                    category.id
                                                                                                                )
                                                                                                                    ? selectedPoiCategories.filter(
                                                                                                                        c => c.id !== category.id
                                                                                                                    )
                                                                                                                    : [
                                                                                                                        ...selectedPoiCategories,
                                                                                                                        category,
                                                                                                                    ];

                                                                                                            setSelectedPoiCategories(
                                                                                                                newCategories
                                                                                                            );
                                                                                                        }}
                                                                                                        className="appearance-none h-5 w-5 rounded-md bg-white border border-gray-300 checked:border-primary-color checked:bg-primary-color checked:border-0 focus:outline-none focus:ring-0 transition-colors"
                                                                                                    />
                                                                                                    <SvgIcons.Check2
                                                                                                        className={`absolute left-0.5 top-0.5 w-4 h-4 text-white pointer-events-none ${selectedPoiCategoryIds.includes(
                                                                                                            category.id
                                                                                                        )
                                                                                                            ? "opacity-100"
                                                                                                            : "opacity-0"
                                                                                                            }`}
                                                                                                        fill="white"
                                                                                                    />
                                                                                                </div>
                                                                                                <label
                                                                                                    htmlFor={`category-${category.id}`}
                                                                                                    className="ml-2.5 text-gray-700 cursor-pointer text-sm"
                                                                                                >
                                                                                                    {category.name}
                                                                                                </label>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Rating Filter - Mobile Only */}
                                                                <div className="py-4 px-4 border-t border-gray-200">
                                                                    <h4 className="font-medium text-sm mb-3 text-gray-700">Rating</h4>
                                                                    <div className="space-y-2">
                                                                        {[
                                                                            { value: 5, label: "5 Stars" },
                                                                            { value: 4, label: "4 Stars & up" },
                                                                            { value: 3, label: "3 Stars & up" },
                                                                            { value: 2, label: "2 Stars & up" },
                                                                        ].map((rating) => (
                                                                            <label
                                                                                key={`mobile-rating-${rating.value}`}
                                                                                className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                                            >
                                                                                <input
                                                                                    type="radio"
                                                                                    name="mobileRatingFilter"
                                                                                    checked={selectedRating === rating.value}
                                                                                    onChange={() => setSelectedRating(selectedRating === rating.value ? null : rating.value)}
                                                                                    className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 focus:ring-primary-color"
                                                                                />
                                                                                <span className="ml-2 text-sm text-gray-700">
                                                                                    {rating.label}
                                                                                </span>
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </div>

                                                                {/* Sort By - Mobile Only */}
                                                                <div className="py-4 px-4 border-t border-gray-200">
                                                                    <h4 className="font-medium text-sm mb-3 text-gray-700">{t('sort_by')}</h4>
                                                                    <select
                                                                        value={sortBy}
                                                                        onChange={(e) => setSortBy(e.target.value)}
                                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-color bg-white"
                                                                    >
                                                                        <option value="">{t('sort_by')}</option>
                                                                        <option value="rating">{t('valai.filter.highestRating')}</option>
                                                                        <option value="rating_count">{t('valai.filter.mostReviews')}</option>
                                                                        <option value="price">{t('valai.filter.price')}</option>
                                                                        <option value="distance">{t('valai.filter.distance')}</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Selected Tags */}
                                        {sortedSelectedTags.length > 0 && (
                                            <div className="flex flex-wrap gap-2 py-3 mt-4">
                                                {sortedSelectedTags.map((tag, index) => (
                                                    <Badge
                                                        key={`selected-tag-${index}-${tag}`}
                                                        name={tag}
                                                        onClose={() => {
                                                            setSelectedTags(prev => prev.filter(t => t !== tag));
                                                        }}
                                                    />
                                                ))}
                                                <button
                                                    className="text-xs bg-gray-200 rounded-full px-3 py-1 text-gray-800 hover:bg-gray-300 transition-colors"
                                                    onClick={() => {
                                                        setSelectedTags([]);
                                                    }}
                                                >
                                                    {t("trips.filters.clearAll")}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {/* Desktop: Search and Date Picker */}
                                        <div className="hidden md:block relative whitespace-nowrap py-4 border-b">
                                            <div className="flex items-center w-full gap-4">
                                                {/* Search Input */}
                                                <div className="flex-1 relative w-full">
                                                    <input
                                                        type="text"
                                                        placeholder={t("trips.myTrips.exploreMore.placeholder")}
                                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:border-primary-color transition-colors"
                                                        value={tourSearchText}
                                                        onChange={(e) => setTourSearchText(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                setCommittedTourSearch(tourSearchText);
                                                            }
                                                        }}
                                                    />
                                                    <SvgIcons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                                    <button
                                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        onClick={() => { setTourSearchText(""); setCommittedTourSearch(""); }}
                                                    >
                                                        {tourSearchText && (
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>

                                                {/* Date Picker */}
                                                <div className="w-auto">
                                                    <ValaiDateRangePicker
                                                        startDate={moment(tourStartDate)}
                                                        endDate={moment(tourEndDate)}
                                                        onDateChange={(start, end) => {
                                                            setTourStartDate(start.format("YYYY-MM-DD"));
                                                            setTourEndDate(end.format("YYYY-MM-DD"));
                                                        }}
                                                        className="min-w-[300px]"
                                                        hideTimeSelect={true}
                                                        maxDays={30}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Mobile: Search Input and Filter Button */}
                                        <div className="md:hidden py-4 border-b">
                                            <div className="flex items-center w-full gap-2">
                                                {/* Search Input */}
                                                <div className="flex-1 relative w-full">
                                                    <input
                                                        type="text"
                                                        placeholder={t("trips.myTrips.exploreMore.placeholder")}
                                                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-full bg-white text-gray-800 placeholder-gray-500 focus:outline-none focus:border-primary-color transition-colors"
                                                        value={tourSearchText}
                                                        onChange={(e) => setTourSearchText(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                setCommittedTourSearch(tourSearchText);
                                                            }
                                                        }}
                                                    />
                                                    <SvgIcons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
                                                    <button
                                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                        onClick={() => { setTourSearchText(""); setCommittedTourSearch(""); }}
                                                    >
                                                        {tourSearchText && <SvgIcons.SearchClose />}
                                                    </button>
                                                </div>

                                                {/* Mobile Filter Button */}
                                                <button
                                                    ref={tourFilterButtonRef}
                                                    onClick={() => setShowTourFilters(!showTourFilters)}
                                                    className="flex items-center justify-center w-12 h-12 bg-white border-2 border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                                                >
                                                    <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                    </svg>
                                                </button>

                                                <div className="relative ml-2">
                                                    {showTourFilters && (
                                                        <>
                                                            <div
                                                                className="fixed inset-0 z-40"
                                                                onClick={() => setShowTourFilters(false)}
                                                                style={{ pointerEvents: "all" }}
                                                            ></div>
                                                            <div
                                                                className="absolute w-80 bg-white rounded-lg shadow-xl z-50 border border-gray-200"
                                                                style={{
                                                                    top: '100%',
                                                                    right: 0,
                                                                    marginTop: '8px',
                                                                    maxHeight: "70vh",
                                                                }}
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <div className="p-4 border-b flex justify-between items-center bg-white z-10 rounded-t-lg">
                                                                    <h3 className="font-semibold text-gray-800">{t("trips.filters.title")}</h3>
                                                                    <div className="flex items-center">
                                                                        <button
                                                                            className="text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 text-gray-700 hover:text-gray-900 transition-colors mr-3"
                                                                            onClick={() => {
                                                                                setSelectedTourCategories([]);
                                                                                setSelectedProviders(TOUR_PROVIDERS.map(p => p.id));
                                                                                setCategoryFilterText("");
                                                                                setTourStartDate("");
                                                                                setTourEndDate("");
                                                                                setShowTourFilters(false);
                                                                            }}
                                                                        >
                                                                            {t("trips.filters.clearAll")}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setShowTourFilters(false)}
                                                                            className="text-gray-400 hover:text-gray-700 transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                                                                        >
                                                                            <SvgIcons.SearchClose
                                                                                width="18"
                                                                                height="18"
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                                                                    <div className="p-4 space-y-4">
                                                                        {/* Date Picker */}
                                                                        <div>
                                                                            <h4 className="font-medium text-sm mb-3 text-gray-700">{t('valai.dates')}</h4>
                                                                            <ValaiDateRangePicker
                                                                                startDate={moment(tourStartDate)}
                                                                                endDate={moment(tourEndDate)}
                                                                                onDateChange={(start, end) => {
                                                                                    setTourStartDate(start.format("YYYY-MM-DD"));
                                                                                    setTourEndDate(end.format("YYYY-MM-DD"));
                                                                                }}
                                                                                className="w-full"
                                                                                hideTimeSelect={true}
                                                                                maxDays={30}
                                                                            />
                                                                        </div>

                                                                        {/* Categories */}
                                                                        <div className="pt-4 border-t border-gray-200">
                                                                            <h4 className="font-medium text-sm mb-3 text-gray-700">{t('valai.categories')}</h4>
                                                                            <input
                                                                                type="text"
                                                                                value={categoryFilterText}
                                                                                onChange={e => setCategoryFilterText(e.target.value)}
                                                                                placeholder="Search categories..."
                                                                                className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-color focus:border-primary-color"
                                                                            />
                                                                            <div className="space-y-1 max-h-64 overflow-y-auto">
                                                                                {!categoryFilterText.trim() && (
                                                                                    <label
                                                                                        className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={isAllSelected}
                                                                                            onChange={handleTourCategoryAllToggle}
                                                                                            className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                        />
                                                                                        <span className="ml-2 text-sm font-medium text-gray-700">
                                                                                            {t("trips.categories.all" as Model.TranslationKey)}
                                                                                        </span>
                                                                                    </label>
                                                                                )}
                                                                                {filteredGroupedCategories.map(cat => {
                                                                                    const mainAllTags = (cat.subCategories ?? []).flatMap(s => s.categories.map(i => i.tag));
                                                                                    const mainAllSelected = mainAllTags.length > 0 && mainAllTags.every(tag => selectedTourCategories.includes(tag));
                                                                                    const mainSomeSelected = mainAllTags.some(tag => selectedTourCategories.includes(tag));
                                                                                    return (
                                                                                        <div key={`mobile-main-${cat.name}`}>
                                                                                            {/* Level 1: Main Category */}
                                                                                            <div className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    ref={el => { if (el) el.indeterminate = mainSomeSelected && !mainAllSelected; }}
                                                                                                    checked={mainAllSelected}
                                                                                                    onChange={() => handleMainCategoryToggle(cat)}
                                                                                                    className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                                />
                                                                                                <span
                                                                                                    className="ml-2 text-sm font-semibold text-gray-800 flex-1"
                                                                                                    onClick={() => toggleTourMainGroup(cat.name)}
                                                                                                >
                                                                                                    {cat.name}
                                                                                                </span>
                                                                                                <svg
                                                                                                    className={`w-4 h-4 text-gray-400 transition-transform ${openTourMainGroups.includes(cat.name) ? 'rotate-180' : ''}`}
                                                                                                    fill="none"
                                                                                                    stroke="currentColor"
                                                                                                    viewBox="0 0 24 24"
                                                                                                    onClick={() => toggleTourMainGroup(cat.name)}
                                                                                                >
                                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                </svg>
                                                                                            </div>
                                                                                            {openTourMainGroups.includes(cat.name) && (cat.subCategories ?? []).map(sub => {
                                                                                                const subAllTags = sub.categories.map(i => i.tag);
                                                                                                const subAllSelected = subAllTags.length > 0 && subAllTags.every(tag => selectedTourCategories.includes(tag));
                                                                                                const subSomeSelected = subAllTags.some(tag => selectedTourCategories.includes(tag));
                                                                                                return (
                                                                                                    <div key={`mobile-sub-${sub.name}`} className="pl-4">
                                                                                                        {/* Level 2: Sub Category */}
                                                                                                        <div className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                                                                                                            <input
                                                                                                                type="checkbox"
                                                                                                                ref={el => { if (el) el.indeterminate = subSomeSelected && !subAllSelected; }}
                                                                                                                checked={subAllSelected}
                                                                                                                onChange={() => handleSubCategoryToggle(sub)}
                                                                                                                className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                                            />
                                                                                                            <span
                                                                                                                className="ml-2 text-sm font-medium text-gray-700 flex-1"
                                                                                                                onClick={() => toggleTourSubGroup(sub.name)}
                                                                                                            >
                                                                                                                {sub.name}
                                                                                                            </span>
                                                                                                            <svg
                                                                                                                className={`w-3 h-3 text-gray-400 transition-transform ${openTourSubGroups.includes(sub.name) ? 'rotate-180' : ''}`}
                                                                                                                fill="none"
                                                                                                                stroke="currentColor"
                                                                                                                viewBox="0 0 24 24"
                                                                                                                onClick={() => toggleTourSubGroup(sub.name)}
                                                                                                            >
                                                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                                            </svg>
                                                                                                        </div>
                                                                                                        {openTourSubGroups.includes(sub.name) && sub.categories.map(catItem => (
                                                                                                            <label
                                                                                                                key={`mobile-tour-category-${catItem.tag}`}
                                                                                                                className="flex items-center cursor-pointer hover:bg-gray-50 p-2 pl-8 rounded-md"
                                                                                                            >
                                                                                                                <input
                                                                                                                    type="checkbox"
                                                                                                                    checked={selectedTourCategories.includes(catItem.tag)}
                                                                                                                    onChange={() => handleTourCategoryToggle(catItem.tag)}
                                                                                                                    className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                                                />
                                                                                                                <span className="ml-2 text-sm text-gray-700 capitalize">
                                                                                                                    {catItem.name}
                                                                                                                </span>
                                                                                                            </label>
                                                                                                        ))}
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </div>

                                                                        {/* Providers */}
                                                                        <div className="pt-4 border-t border-gray-200">
                                                                            <h4 className="font-medium text-sm mb-3 text-gray-700">{t('trips.filters.providers')}</h4>
                                                                            <div className="space-y-2">
                                                                                {TOUR_PROVIDERS.map(provider => (
                                                                                    <label
                                                                                        key={`mobile-provider-${provider.id}`}
                                                                                        className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                                                    >
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={selectedProviders.includes(provider.id)}
                                                                                            onChange={() => handleProviderChange(provider.id)}
                                                                                            className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                        />
                                                                                        <span className="ml-2 text-sm text-gray-700">{provider.name}</span>
                                                                                    </label>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Results Section with Sidebar */}
                        {activeTab === 'things-to-do' ? (
                            <div className="block md:flex md:gap-6">
                                {/* Left Sidebar - Filters - Hidden on mobile */}
                                <div className="hidden md:block w-64 flex-shrink-0">
                                    <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="font-semibold text-lg">{t('trips.filters.title')}</h3>
                                            {(selectedPoiCategoryIds.length > 0 || selectedRating !== null) && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedPoiCategories([]);
                                                        setSelectedRating(null);
                                                    }}
                                                    className="text-sm text-primary-color hover:underline"
                                                >
                                                    {t('trips.filters.clearAll')}
                                                </button>
                                            )}
                                        </div>

                                        <div className="space-y-4">
                                            {/* Rating Filter */}
                                            <div className="pb-4 border-b border-gray-200">
                                                <h4 className="font-medium text-sm mb-3 text-gray-700">
                                                    Rating
                                                </h4>
                                                <div className="space-y-2">
                                                    {[
                                                        { value: 5, label: "5 Stars" },
                                                        { value: 4, label: "4 Stars & up" },
                                                        { value: 3, label: "3 Stars & up" },
                                                        { value: 2, label: "2 Stars & up" },
                                                    ].map((rating) => (
                                                        <label
                                                            key={`rating-${rating.value}`}
                                                            className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="ratingFilter"
                                                                checked={selectedRating === rating.value}
                                                                onChange={() => setSelectedRating(selectedRating === rating.value ? null : rating.value)}
                                                                className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 focus:ring-primary-color"
                                                            />
                                                            <span className="ml-2 text-sm text-gray-700">
                                                                {rating.label}
                                                            </span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Category Groups with Accordion */}
                                            {poiCategoryGroups.map(group => (
                                                <div key={`sidebar-group-${group.name}`}>
                                                    <h4
                                                        onClick={() => toggleSidebarGroup(group.name)}
                                                        className="font-medium text-sm mb-3 text-gray-700 flex items-center justify-between cursor-pointer"
                                                    >
                                                        {group.name}
                                                        <svg
                                                            className={`w-4 h-4 transition-transform ${openSidebarGroups.includes(group.name) ? 'rotate-180' : ''}`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </h4>
                                                    {openSidebarGroups.includes(group.name) && (
                                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                                            {group.categories.map(category => (
                                                                <label
                                                                    key={`sidebar-category-${category.id}`}
                                                                    className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedPoiCategoryIds.includes(category.id)}
                                                                        onChange={() => {
                                                                            const newCategories =
                                                                                selectedPoiCategoryIds.includes(category.id)
                                                                                    ? selectedPoiCategories.filter(c => c.id !== category.id)
                                                                                    : [...selectedPoiCategories, category];
                                                                            setSelectedPoiCategories(newCategories);
                                                                        }}
                                                                        className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                    />
                                                                    <span className="ml-2 text-sm text-gray-700">{category.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Content - Results */}
                                <div className="flex-1">
                                    {/* Sort and Result Count */}
                                    <div className="mb-6">
                                        <div className="flex justify-between items-center">
                                            <div className="text-sm text-gray-500">
                                                Showing {searchResults?.length || 0} of <strong className="text-primary-color">{pagination?.total || 0} places</strong>
                                            </div>
                                            {/* Sort By - Desktop Only */}
                                            <div className="hidden md:flex items-center gap-2">
                                                <select
                                                    value={sortBy}
                                                    onChange={(e) => setSortBy(e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-color bg-white"
                                                >
                                                    <option value="">{t('sort_by')}</option>
                                                    <option value="rating">{t('valai.filter.highestRating')}</option>
                                                    <option value="rating_count">{t('valai.filter.mostReviews')}</option>
                                                    <option value="price">{t('valai.filter.price')}</option>
                                                    <option value="distance">{t('valai.filter.distance')}</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Results List */}
                                    {searchLoading ? (
                                        <div className="flex items-center justify-center py-20">
                                            <PreLoading />
                                        </div>
                                    ) : searchResults && searchResults.length > 0 ? (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-1 gap-3">
                                                {searchResults.map((poi) => (
                                                    <div
                                                        key={poi.id}
                                                        onClick={() => window.open(`/place/${poi.id}`, '_blank')}
                                                        className="bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow duration-200 cursor-pointer group border border-gray-200"
                                                    >
                                                        {/* Mobile: Vertical Layout, Desktop: Horizontal Layout */}
                                                        <div className="flex flex-col md:flex-row md:gap-4 md:p-4">
                                                            {/* Image Container */}
                                                            <div className="relative w-full md:w-32 h-48 md:h-32 md:flex-shrink-0 md:rounded-lg overflow-hidden">
                                                                <img
                                                                    src={poi.image?.url || 'https://via.placeholder.com/300x200'}
                                                                    alt={poi.name}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                {/* Heart Icon - Absolute on mobile, separate on desktop */}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleFavoriteToggle(poi.id.toString());
                                                                    }}
                                                                    disabled={loadingFavoritePoi(poi.id.toString())}
                                                                    className="absolute top-2 right-2 md:hidden p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                                                                >
                                                                    {loadingFavoritePoi(poi.id.toString()) ? (
                                                                        <svg className="w-5 h-5 animate-spin text-primary-color" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                    ) : (
                                                                        <SvgIcons.Hearth className={`w-5 h-5 ${isPoiFavorite(poi.id.toString()) ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                                                                    )}
                                                                </button>
                                                            </div>

                                                            {/* Content */}
                                                            <div className="flex-1 flex justify-between p-4 md:p-0">
                                                                <div className="flex flex-col flex-1">
                                                                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                                                                        {poi.name}
                                                                    </h3>

                                                                    {/* Rating */}
                                                                    <div className="flex items-center mb-3">
                                                                        <div className="flex items-center">
                                                                            {[...Array(5)].map((_, i) => (
                                                                                <SvgIcons.Star
                                                                                    key={i}
                                                                                    className={`w-3.5 h-3.5 ${i < Math.floor(poi.rating || 0) ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                                                                />
                                                                            ))}
                                                                        </div>
                                                                        <span className="text-xs text-gray-500 ml-2">
                                                                            {poi.ratingCount || 0} {t('reviews')}
                                                                        </span>
                                                                    </div>

                                                                    {/* Badges */}
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {poi.category?.[0]?.name && (
                                                                            <span className="px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded-full">
                                                                                {poi.category[0].name}
                                                                            </span>
                                                                        )}
                                                                        <span className="px-3 py-1 border border-gray-300 text-gray-700 text-xs rounded-full">
                                                                            {t('tours')}
                                                                        </span>
                                                                    </div>
                                                                </div>

                                                                {/* Favorite Icon - Desktop only */}
                                                                <div className="hidden md:flex items-start">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleFavoriteToggle(poi.id.toString());
                                                                        }}
                                                                        disabled={loadingFavoritePoi(poi.id.toString())}
                                                                        className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                                                                    >
                                                                        {loadingFavoritePoi(poi.id.toString()) ? (
                                                                            <svg className="w-5 h-5 animate-spin text-primary-color" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                            </svg>
                                                                        ) : (
                                                                            <SvgIcons.Hearth className={`w-5 h-5 ${isPoiFavorite(poi.id.toString()) ? 'text-red-500 fill-current' : 'text-gray-400'}`} />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Pagination */}
                                            {pagination && pagination.totalPages > 1 && (
                                                <div className="flex items-center justify-between mt-8">
                                                    {/* Previous Button */}
                                                    <button
                                                        onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                                                        disabled={currentPage === 1}
                                                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                                    >
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                        </svg>
                                                        <span className="hidden md:inline">{t('trips.createNewTrip.form.previous')}</span>
                                                    </button>

                                                    {/* Page Display - Mobile: Text, Desktop: Numbers */}
                                                    {/* Mobile: Simple page text */}
                                                    <div className="md:hidden text-sm text-gray-700">
                                                        Page {currentPage} of {pagination.totalPages}
                                                    </div>

                                                    {/* Desktop: Page Numbers */}
                                                    <div className="hidden md:flex items-center gap-2">
                                                        {/* First 3 pages */}
                                                        {[1, 2, 3].map(pageNum => {
                                                            if (pageNum > pagination.totalPages) return null;
                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                    className={`w-8 h-8 text-sm rounded ${currentPage === pageNum
                                                                        ? 'bg-blue-100 text-blue-600'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        })}

                                                        {/* Ellipsis */}
                                                        {pagination.totalPages > 6 && (
                                                            <span className="text-gray-500 px-1">...</span>
                                                        )}

                                                        {/* Last 3 pages */}
                                                        {pagination.totalPages > 6 && [pagination.totalPages - 2, pagination.totalPages - 1, pagination.totalPages].map(pageNum => {
                                                            if (pageNum <= 3) return null;
                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                    className={`w-8 h-8 text-sm rounded ${currentPage === pageNum
                                                                        ? 'bg-blue-100 text-blue-600'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        })}

                                                        {/* Pages 4-6 when total is <= 6 */}
                                                        {pagination.totalPages <= 6 && [4, 5, 6].map(pageNum => {
                                                            if (pageNum > pagination.totalPages) return null;
                                                            return (
                                                                <button
                                                                    key={pageNum}
                                                                    onClick={() => setCurrentPage(pageNum)}
                                                                    className={`w-8 h-8 text-sm rounded ${currentPage === pageNum
                                                                        ? 'bg-blue-100 text-blue-600'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                >
                                                                    {pageNum}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Next Button */}
                                                    <button
                                                        onClick={() => currentPage < pagination.totalPages && setCurrentPage(currentPage + 1)}
                                                        disabled={currentPage === pagination.totalPages}
                                                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${currentPage === pagination.totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                                    >
                                                        Next
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-20">
                                            <p className="text-gray-500">{t('trips.myTrips.exploreMore.noResultsFound')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="block md:flex md:gap-6">
                                {/* Left Sidebar - Categories and Providers - Hidden on mobile */}
                                <div className="hidden md:block w-64 flex-shrink-0">
                                    <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="font-semibold text-lg">{t("trips.filters.title")}</h3>
                                            <button
                                                onClick={() => {
                                                    setSelectedTourCategories([]);
                                                    setSelectedProviders(TOUR_PROVIDERS.map(p => p.id));
                                                    setCategoryFilterText("");
                                                }}
                                                className="text-sm text-primary-color hover:underline"
                                            >
                                                {t('trips.filters.clearAll')}
                                            </button>
                                        </div>

                                        <div className="space-y-4">
                                            {/* Categories */}
                                            <div>
                                                <h4 className="font-medium text-sm mb-3 text-gray-700">
                                                    {t('valai.categories')}
                                                </h4>
                                                <input
                                                    type="text"
                                                    value={categoryFilterText}
                                                    onChange={e => setCategoryFilterText(e.target.value)}
                                                    placeholder="Search categories..."
                                                    className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-color focus:border-primary-color"
                                                />
                                                <div className="space-y-1 max-h-96 overflow-y-auto">
                                                    {!categoryFilterText.trim() && (
                                                        <label
                                                            className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isAllSelected}
                                                                onChange={handleTourCategoryAllToggle}
                                                                className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                            />
                                                            <span className="ml-2 text-sm font-medium text-gray-700">
                                                                {t("trips.categories.all" as Model.TranslationKey)}
                                                            </span>
                                                        </label>
                                                    )}
                                                    {filteredGroupedCategories.map(cat => {
                                                        const mainAllTags = (cat.subCategories ?? []).flatMap(s => s.categories.map(i => i.tag));
                                                        const mainAllSelected = mainAllTags.length > 0 && mainAllTags.every(tag => selectedTourCategories.includes(tag));
                                                        const mainSomeSelected = mainAllTags.some(tag => selectedTourCategories.includes(tag));
                                                        return (
                                                            <div key={`sidebar-main-${cat.name}`}>
                                                                {/* Level 1: Main Category */}
                                                                <div className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                                                                    <input
                                                                        type="checkbox"
                                                                        ref={el => { if (el) el.indeterminate = mainSomeSelected && !mainAllSelected; }}
                                                                        checked={mainAllSelected}
                                                                        onChange={() => handleMainCategoryToggle(cat)}
                                                                        className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                    />
                                                                    <span
                                                                        className="ml-2 text-sm font-semibold text-gray-800 flex-1"
                                                                        onClick={() => toggleTourMainGroup(cat.name)}
                                                                    >
                                                                        {cat.name}
                                                                    </span>
                                                                    <svg
                                                                        className={`w-4 h-4 text-gray-400 transition-transform ${openTourMainGroups.includes(cat.name) ? 'rotate-180' : ''}`}
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        onClick={() => toggleTourMainGroup(cat.name)}
                                                                    >
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                    </svg>
                                                                </div>
                                                                {openTourMainGroups.includes(cat.name) && (cat.subCategories ?? []).map(sub => {
                                                                    const subAllTags = sub.categories.map(i => i.tag);
                                                                    const subAllSelected = subAllTags.length > 0 && subAllTags.every(tag => selectedTourCategories.includes(tag));
                                                                    const subSomeSelected = subAllTags.some(tag => selectedTourCategories.includes(tag));
                                                                    return (
                                                                        <div key={`sidebar-sub-${sub.name}`} className="pl-4">
                                                                            {/* Level 2: Sub Category */}
                                                                            <div className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    ref={el => { if (el) el.indeterminate = subSomeSelected && !subAllSelected; }}
                                                                                    checked={subAllSelected}
                                                                                    onChange={() => handleSubCategoryToggle(sub)}
                                                                                    className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                />
                                                                                <span
                                                                                    className="ml-2 text-sm font-medium text-gray-700 flex-1"
                                                                                    onClick={() => toggleTourSubGroup(sub.name)}
                                                                                >
                                                                                    {sub.name}
                                                                                </span>
                                                                                <svg
                                                                                    className={`w-3 h-3 text-gray-400 transition-transform ${openTourSubGroups.includes(sub.name) ? 'rotate-180' : ''}`}
                                                                                    fill="none"
                                                                                    stroke="currentColor"
                                                                                    viewBox="0 0 24 24"
                                                                                    onClick={() => toggleTourSubGroup(sub.name)}
                                                                                >
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                                                </svg>
                                                                            </div>
                                                                            {openTourSubGroups.includes(sub.name) && sub.categories.map(catItem => (
                                                                                <label
                                                                                    key={`sidebar-tour-category-${catItem.tag}`}
                                                                                    className="flex items-center cursor-pointer hover:bg-gray-50 p-2 pl-8 rounded-md"
                                                                                >
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={selectedTourCategories.includes(catItem.tag)}
                                                                                        onChange={() => handleTourCategoryToggle(catItem.tag)}
                                                                                        className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                                                    />
                                                                                    <span className="ml-2 text-sm text-gray-700 capitalize">
                                                                                        {catItem.name}
                                                                                    </span>
                                                                                </label>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Providers */}
                                            <div className="pt-4 border-t border-gray-200">
                                                <h4 className="font-medium text-sm mb-3 text-gray-700">
                                                    {t('trips.filters.providers')}
                                                </h4>
                                                <div className="space-y-2">
                                                    {TOUR_PROVIDERS.map(provider => (
                                                        <label
                                                            key={`sidebar-provider-${provider.id}`}
                                                            className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedProviders.includes(provider.id)}
                                                                onChange={() => handleProviderChange(provider.id)}
                                                                className="w-4 h-4 accent-[var(--primary-color)] border-gray-300 rounded focus:ring-primary-color"
                                                            />
                                                            <span className="ml-2 text-sm text-gray-700">{provider.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Content - Tours */}
                                <div className="flex-1 flex flex-col">
                                    {selectedProviders.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                                            <svg className="w-12 h-12 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <span className="text-center text-sm font-medium">Please select at least one provider to view tours.</span>
                                        </div>
                                    ) : (
                                    <>
                                    {/* Tour Count Display */}
                                    {totalLoaded > 0 && (
                                        <div className="py-4 border-b">
                                            <div className="text-sm text-gray-500">
                                                {isExhausted
                                                    ? <>Showing {combinedTours.length} of <strong className="text-primary-color">{totalLoaded} tours</strong></>
                                                    : <>Showing <strong className="text-primary-color">{combinedTours.length} tours</strong></>
                                                }
                                            </div>
                                        </div>
                                    )}

                                    {/* Tours List */}
                                    <div className="flex-1 overflow-auto py-4">
                                        {combinedLoading ? (
                                            <div className="flex items-center justify-center py-20">
                                                <PreLoading />
                                            </div>
                                        ) : combinedTours.length === 0 && currentTourPage === 1 ? (
                                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                                <span className="text-center text-text-primary-color text-sm min-h-16">
                                                    {t("trips.myTrips.exploreMore.noResultsFound")}
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="space-y-4">
                                                    {combinedTours.map(tour => {
                                                        const coverImage = tour.images?.find(img => img.is_cover)?.url || tour.images?.[0]?.url;
                                                        const isViator = tour.providerId === Model.PROVIDER_ID.VIATOR;

                                                        const formatDuration = (minutes: number): string => {
                                                            if (minutes < 60) return `${minutes} mins`;
                                                            const hours = Math.floor(minutes / 60);
                                                            const remaining = minutes % 60;
                                                            return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
                                                        };

                                                        return (
                                                            <div
                                                                key={`tour-${tour.providerId}-${tour.productId}`}
                                                                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
                                                                onClick={() => {
                                                                    handleTourClick(tour.providerId, tour.productId, {
                                                                        url: tour.url,
                                                                    });
                                                                }}
                                                            >
                                                                <div className="block md:flex md:gap-4 md:p-4">
                                                                    {/* Image */}
                                                                    <div className={`w-full h-48 md:w-48 md:h-32 flex-shrink-0 md:rounded-lg overflow-hidden ${coverImage ? 'bg-gray-100' : 'bg-gradient-to-br from-purple-600 to-blue-600'}`}>
                                                                        {coverImage ? (
                                                                            <img
                                                                                src={coverImage}
                                                                                alt={tour.title}
                                                                                className="w-full h-full object-cover"
                                                                            />
                                                                        ) : (
                                                                            <div className="w-full h-full flex items-center justify-center">
                                                                                <span className="text-white text-2xl font-bold">{tour.title?.[0] || 'T'}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Content */}
                                                                    <div className="flex-1 p-4 md:p-0">
                                                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between h-full">
                                                                            <div className="flex flex-col gap-2">
                                                                                {/* Provider */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex items-center gap-2">
                                                                                        {isViator ? (
                                                                                            <>
                                                                                                <img src={viatorLogo} alt="Viator" className="w-5 h-5" />
                                                                                                <span className="text-sm text-gray-500 ml-1.5">Viator</span>
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <div className="w-6 h-6 bg-purple-600 rounded flex items-center justify-center">
                                                                                                    <span className="text-white text-xs font-bold">V</span>
                                                                                                </div>
                                                                                                <span className="text-sm text-gray-600">Victory Live</span>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Title */}
                                                                                <h3 className="font-semibold text-gray-900 line-clamp-2">
                                                                                    {tour.title}
                                                                                </h3>

                                                                                {/* Rating */}
                                                                                {tour.rating > 0 && (
                                                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                                        <SvgIcons.Star className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                                                                                        <span>{tour.rating.toFixed(1)}</span>
                                                                                        {tour.ratingCount > 0 && (
                                                                                            <span className="text-xs text-gray-400">({tour.ratingCount})</span>
                                                                                        )}
                                                                                    </div>
                                                                                )}

                                                                                {/* Duration */}
                                                                                {tour.duration > 0 && (
                                                                                    <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                                        </svg>
                                                                                        <span>{formatDuration(tour.duration)}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            {/* Price and Book Button */}
                                                                            <div className="flex md:flex-row items-center md:items-end justify-between md:justify-start gap-2 mt-3 md:mt-0">
                                                                                {tour.price > 0 && (
                                                                                    <div className="text-left md:text-right flex items-center">
                                                                                        <div className="text-xs text-gray-500">From</div>
                                                                                        <div className="text-xl font-bold text-gray-900 ml-1">
                                                                                            ${tour.price.toFixed(2)}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                                <button
                                                                                    className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-medium"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleTourClick(tour.providerId, tour.productId, {
                                                                                            url: tour.url,
                                                                                        });
                                                                                    }}
                                                                                >
                                                                                    {t('trips.poi.book')}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                {/* Pagination */}
                                                {(currentTourPage > 1 || hasNextPage) && (
                                                    <div className="flex items-center justify-between mt-8">
                                                        {/* Previous Button */}
                                                        <button
                                                            onClick={() => currentTourPage > 1 && setCurrentTourPage(currentTourPage - 1)}
                                                            disabled={currentTourPage === 1}
                                                            className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${currentTourPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                            </svg>
                                                            <span className="hidden md:inline">{t('trips.createNewTrip.form.previous')}</span>
                                                        </button>

                                                        {/* Page Display - Mobile: Text, Desktop: Numbers */}
                                                        {/* Mobile: Simple page text */}
                                                        <div className="md:hidden text-sm text-gray-700">
                                                            Page {currentTourPage}{totalTourPages ? ` of ${totalTourPages}` : ''}
                                                        </div>

                                                        {/* Desktop: Page Numbers */}
                                                        <div className="hidden md:flex items-center gap-2">
                                                            {totalTourPages ? (
                                                                <>
                                                                    {/* First 3 pages */}
                                                                    {[1, 2, 3].map(pageNum => {
                                                                        if (pageNum > totalTourPages) return null;
                                                                        return (
                                                                            <button
                                                                                key={pageNum}
                                                                                onClick={() => setCurrentTourPage(pageNum)}
                                                                                className={`w-8 h-8 text-sm rounded ${currentTourPage === pageNum
                                                                                    ? 'bg-blue-100 text-blue-600'
                                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                                    }`}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        );
                                                                    })}

                                                                    {/* Ellipsis */}
                                                                    {totalTourPages > 6 && (
                                                                        <span className="text-gray-500 px-1">...</span>
                                                                    )}

                                                                    {/* Last 3 pages */}
                                                                    {totalTourPages > 6 && [totalTourPages - 2, totalTourPages - 1, totalTourPages].map(pageNum => {
                                                                        if (pageNum <= 3) return null;
                                                                        return (
                                                                            <button
                                                                                key={pageNum}
                                                                                onClick={() => setCurrentTourPage(pageNum)}
                                                                                className={`w-8 h-8 text-sm rounded ${currentTourPage === pageNum
                                                                                    ? 'bg-blue-100 text-blue-600'
                                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                                    }`}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        );
                                                                    })}

                                                                    {/* Pages 4-6 when total is <= 6 */}
                                                                    {totalTourPages <= 6 && [4, 5, 6].map(pageNum => {
                                                                        if (pageNum > totalTourPages) return null;
                                                                        return (
                                                                            <button
                                                                                key={pageNum}
                                                                                onClick={() => setCurrentTourPage(pageNum)}
                                                                                className={`w-8 h-8 text-sm rounded ${currentTourPage === pageNum
                                                                                    ? 'bg-blue-100 text-blue-600'
                                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                                    }`}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </>
                                                            ) : (
                                                                <span className="text-sm text-gray-700">
                                                                    Page {currentTourPage}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Next Button */}
                                                        <button
                                                            onClick={() => hasNextPage && setCurrentTourPage(currentTourPage + 1)}
                                                            disabled={!hasNextPage}
                                                            className={`flex items-center gap-1 px-3 py-1 text-sm rounded ${!hasNextPage ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                                                        >
                                                            {t('trips.myTrips.localExperiences.tourDetails.next')}
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Footer t={t} />

            {/* Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
                        <div className="absolute top-4 right-4">
                            <CloseIconButton2
                                fill="#2B2B33"
                                clicked={() => {
                                    setShowAuthModal(false);
                                    setPendingFavoritePoiId(null);
                                }}
                                rounded
                            />
                        </div>
                        <div className="mt-4">
                            {authModalType === 'register' ? (
                                <FormTemplateRegister
                                    register={registerEmail}
                                    successRegister={callbackSuccessRegister}
                                    reCaptchaSiteKey={window.tconfig.RECAPTCHA_SITE_KEY}
                                    showCaptcha={process.env.NODE_ENV === "production"}
                                    goBackLogin={() => setAuthModalType('login')}
                                    showRegister={window.tconfig.SHOW_REGISTER}
                                    registerFields={window.tconfig.REGISTER_FIELDS}
                                    socialLoginConfigs={{
                                        clientId: window.tconfig.COGNITO.CLIENT_ID,
                                        domain: window.tconfig.COGNITO.DOMAIN,
                                        identityProviders: window.tconfig.COGNITO.IDENTITY_PROVIDERS,
                                        region: window.tconfig.COGNITO.REGION,
                                        baseDomain: `${window.tconfig.DOMAIN_ORIGIN}${window.tconfig.DOMAIN_ROUTER_BASE_NAME}`,
                                    }}
                                    t={t}
                                />
                            ) : (
                                <FormTemplateLogin
                                    login={(email, password, captchaToken) =>
                                        api.loginEmail(email, password, undefined, captchaToken)
                                    }
                                    successLogin={callbackSuccessLogin}
                                    reCaptchaSiteKey={window.tconfig.RECAPTCHA_SITE_KEY}
                                    showCaptcha={process.env.NODE_ENV === "production"}
                                    showRegister={window.tconfig.SHOW_REGISTER}
                                    socialLoginConfigs={{
                                        clientId: window.tconfig.COGNITO.CLIENT_ID,
                                        domain: window.tconfig.COGNITO.DOMAIN,
                                        identityProviders: window.tconfig.COGNITO.IDENTITY_PROVIDERS,
                                        region: window.tconfig.COGNITO.REGION,
                                        baseDomain: `${window.tconfig.DOMAIN_ORIGIN}${window.tconfig.DOMAIN_ROUTER_BASE_NAME}`,
                                    }}
                                    forgotPassword={() => navigate("/forgot-password")}
                                    signUpButtonCallBack={() => setAuthModalType('register')}
                                    t={t}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetailPoiAndTour;